import React from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {batchPickCandidates, checkKeyOf, stockShortItems, WH_LAHR} from './wizard';
import {availCat} from './availability';

// Bestands-Leiste (Original `renderStockBar`, Zeile 3838). Sie gehört zu zwei
// Schritten und zeigt in beiden etwas anderes:
//
//  - Schritt 1 „Bestand": reine Orientierung — der Text erklärt den
//    Schieberegler „Liefern ab" und nennt die Zahl der knappen Positionen.
//  - Schritt 6 „Kommissionierung": Erklärung der drei Positions-Aktionen, der
//    Prüf-Fortschritt, die Warnung über ungeprüfte Fehlmengen und der
//    Sammelknopf „Alle Lahr-Positionen reservieren".
//
// ABGLEICH E66–E70, G27, G29.

export {stockShortItems};

export default function StockBar({
    rows,
    stock,
    order,
    mode,
    step = 1,
    checked = [],
    lahrGesperrt = false,
    canWarehouse = true,
    onBatchPick,
}) {
    if (mode !== 'order' || !order || !stock.loaded) {
        return null;
    }

    const short = stockShortItems(rows, stock, order);

    // ── Schritt 1: nur Orientierung ─────────────────────────────────────────
    if (step !== 6) {
        return (
            <div className="fbar" style={{display: 'flex'}}>
                <span className="lbl">
                    Bestand:
                    <HelpTooltip
                        id="oe-stockbar-help"
                        placement="top"
                        size={0.7}
                        detail={<>
                            Was dieser Auftrag selbst schon reserviert hat, bleibt verfügbar. Aufträge reservieren
                            Bestand. Offene, nicht reservierte Mengen anderer Aufträge werden nicht abgezogen und stehen
                            nur grau an der Position. Die Zahlen werden beim Öffnen des Belegs live aus weclapp
                            gerechnet, nicht aus dem stündlichen Abgleich des Ordermanagements.
                        </>}
                    >
                        Verfügbar ist der Lagerbestand im Lieferlager der Position abzüglich der von anderen
                        Aufträgen und Lieferscheinen reservierten Menge.
                    </HelpTooltip>
                </span>
                <span className="muted">
                    Je Position mit dem Schieberegler <b>„Liefern ab"</b> entscheiden, was ab <b>Lahr</b> und was ab{' '}
                    <b>Schneider</b> läuft.
                    {short.length
                        ? <> <b>{short.length}</b> Position(en) sind laut Bestand knapp — das klärst du später in Schritt 6 (nach der Kommissionierung).</>
                        : ' Alles ist laut Bestand lieferbar.'}
                </span>
                {stock.lahrStockTrusted === false ? (
                    <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                        ⚠ Der Lahr-Bestand wird in weclapp noch aufgebaut — die dortigen Werte können Platzhalter sein.
                    </span>
                ) : null}
            </div>
        );
    }

    // ── Schritt 6: Kommissionier-Ergebnis ───────────────────────────────────
    const relevant = rows.filter((r) => !r.del && !r.locked && !r._new && r.articleId);
    const gesetzt = new Set(checked);
    const geprueft = relevant.filter((r) => gesetzt.has(checkKeyOf(r))).length;
    const naUngeprueft = relevant.filter(
        (r) => availCat(r, stock, order) === 0 && !gesetzt.has(checkKeyOf(r)),
    ).length;

    const kandidaten = batchPickCandidates(rows, stock, order);
    const falschesLager = order.warehouseId && String(order.warehouseId) !== WH_LAHR;

    return (
        <div className="fbar" style={{display: 'flex'}}>
            <span className="lbl">
                Bestand:
                <HelpTooltip
                    id="oe-stockbar-komm-help"
                    placement="top"
                    size={0.7}
                    detail={<>
                        „Reservieren" sichert verfügbare Ware für diesen Auftrag (höchstens Lagerbestand minus von
                        anderen Aufträgen reserviert), ohne den Bestand zu verändern. „Doch da" und „nicht da" sind
                        echte Lagerbewegungen und erscheinen im Wochenreport. Aufträge reservieren Bestand — die Zahl
                        an jeder Position nennt die Belege.
                    </>}
                >
                    Hier wird das Ergebnis der Kommissionierung eingetragen: reservieren, „doch da" oder „nicht da".
                </HelpTooltip>
            </span>

            {short.length ? (
                <span className="muted">
                    {short.length} Position(en) laut Bestand nicht/teilweise verfügbar — pro Position:{' '}
                    <b>🔒 reservieren</b> (überbucht, aber physisch da), <b>✓ doch da (Bestand +)</b> (verfügbar
                    ist 0), <b>✕ nicht da</b> (senkt auf die fremd reservierte Menge). Jede geprüfte Position mit „geprüft?" abhaken.
                </span>
            ) : (
                <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>
                    ✓ Alle Positionen sind laut Bestand lieferbar.
                </span>
            )}

            {relevant.length ? (
                <>
                    <span className="sp"/>
                    <OeTip tip="Zahl der Positionen, die im Lager physisch geprüft wurden. Der Haken liegt im Ordermanagement und ist für alle Bearbeiter sichtbar.">
                        <span className="poinfo" style={{display: 'inline'}}>
                            ✓ geprüft: <b>{geprueft} / {relevant.length}</b> Positionen
                        </span>
                    </OeTip>
                    {naUngeprueft ? (
                        <OeTip tip="Positionen ohne Bestand, die noch nicht abgehakt sind — bei ihnen ist offen, ob die Ware im Lager doch liegt.">
                            <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                                ⚠ {naUngeprueft} nicht-verfügbare noch nicht geprüft
                            </span>
                        </OeTip>
                    ) : null}
                </>
            ) : null}

            <span className="sp"/>

            {falschesLager ? (
                // whiteSpace: .poinfo steht global auf nowrap (für kurze Kürzel wie
                // „nicht bestellt"). Dieser Satz ist lang und liefe damit bei 1440 px
                // rechts aus dem Bild — deshalb hier ausdrücklich umbrechen lassen.
                <span className="poinfo" style={{color: 'var(--warn)', display: 'inline', whiteSpace: 'normal'}}>
                    Reservieren geht nur am Auftragslager — der Auftrag steht auf <b>{order.warehouseName || ''}</b>.
                    {' '}Fürs Reservieren das Auftragslager in <b>Schritt 1</b> („alle Lahr") auf Lahr stellen und
                    speichern; beim Lahr-Lieferschein (Schritt 9) setzt der Server es ohnehin auf Lahr.
                </span>
            ) : kandidaten.length ? (
                <OeTip
                    tip={lahrGesperrt
                        ? 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe'
                        : `Reserviert den verfügbaren Lahr-Bestand für alle ${kandidaten.length} Lahr-Positionen in EINEM Vorgang; vorher kommt eine Rückfrage.`}
                    detail={lahrGesperrt
                        ? undefined
                        : 'Reserviert wird höchstens so viel, wie nicht schon von anderen Aufträgen reserviert ist; knappe Positionen werden einzeln gemeldet, der Rest wird reserviert. '
                            + 'Danach ist die Ware für diesen Auftrag gesperrt und die Lieferscheine gehen durch. Der physische Bestand ändert sich nicht.'}
                >
                    <button
                        type="button"
                        className="actbtn"
                        disabled={lahrGesperrt || !canWarehouse}
                        onClick={() => onBatchPick(kandidaten)}
                    >
                        🔒 Alle {kandidaten.length} Lahr-Positionen für diesen Auftrag reservieren
                    </button>
                </OeTip>
            ) : (
                <span className="poinfo" style={{color: 'var(--ok)', display: 'inline'}}>
                    🔒 Alle Lahr-Positionen sind bereits reserviert.
                </span>
            )}

            {stock.lahrStockTrusted === false ? (
                <span className="poinfo" style={{color: 'var(--warn)', display: 'inline'}}>
                    ⚠ Der Lahr-Bestand wird in weclapp noch aufgebaut — Reservierungen und Buchungen in Lahr sind
                    gesperrt, bis die Pflege abgeschlossen ist.
                </span>
            ) : null}
        </div>
    );
}
